import * as React from 'react';
import NextLink from 'next/link';
import QRCode from 'react-qr-code';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import axios from '../../utils/axios';
import { HistoricoGPS } from '../historicoGPS';
import { Num, SeletorStatus, statusTexto } from './index';
import ModalPassos, { PassoModal } from './ModalPassos';
import { Campo, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';
import { AutoCompleteUnico, CampoLupa, buscarPaginado, OpcaoAuto } from './AutoComplete';
import ModalSelecionar from './ModalSelecionar';

/* ============================================================================
   Cadastro de GRUPO (entidade) em modal de passos.

   Substitui /dashboard/entidades/new/:id para criar e editar. A pagina antiga
   continua no ar e nao foi tocada.

   Conferido campo por campo contra pages/dashboard/entidades/new/[id].tsx.
   Estao aqui: nome, descricao, regional, status, a lista de APKs do grupo, os
   dois interruptores de provisionamento, o QR Code e o historico.

   QUATRO COISAS DO FORMULARIO ANTIGO QUE PRECISAM CONTINUAR EXATAMENTE ASSIM:

   1. `aplicativosDadosMoveis` NAO E ENVIADO ao servidor. O formulario antigo faz
      `delete data.aplicativosDadosMoveis` antes do POST. O campo existe apenas
      para RECALCULAR O QR CODE - ele mexe na chave
      PROVISIONING_USE_MOBILE_DATA do token, e nao em coluna de banco. Mandar
      esse campo seria inventar comportamento novo.

   2. O QR CODE E O `tokenGoogle` DA POLITICA, alterado em memoria. Os dois
      interruptores ligam/desligam duas chaves do JSON; quando desmarcados, a
      chave e REMOVIDA (nao vai `false`), porque e assim que o provisionador do
      Android le. Copiei a logica linha a linha.

   3. `dataCampo` E A TRILHA DE AUDITORIA e so e montada na EDICAO (no cadastro
      novo o antigo manda `null`). Sem ela a alteracao entra sem historico.

   4. A REGIONAL vai como `codRegional`, e so quando ha regional escolhida -
      `data?.regional?.nome ? codigo : null`. O envio continua assim; o que mudou
      em 28/09/2026 e que a TELA passou a exigir a escolha (ver o passo
      'regional'), entao na pratica o `null` nao sai mais daqui.

   Uma coisa que o formulario antigo tinha e NAO foi copiada: o estado `tipo`
   ('FORCE_INSTALLED'/'AVAILABLE') do seletor de APK. Ele era gravado no estado e
   NUNCA entrava no objeto enviado (`dataEnviar` nao tem `tipo`) - era codigo
   morto. Reproduzi-lo seria copiar um campo que nao faz nada.
   ============================================================================ */

type Regional = { codigo: number; nome: string };
type Apk = { codigo: number; nome?: string; packageName?: string; url?: string; novo?: boolean };

type Formulario = {
  nome: string;
  descricao: string;
  status: string;
  regional: Regional | null;
  aplicativosBasicos: boolean;
  aplicativosDadosMoveis: boolean;
};

const VAZIO: Formulario = {
  nome: '',
  descricao: '',
  status: 'ATIVO',
  regional: null,
  aplicativosBasicos: false,
  aplicativosDadosMoveis: false,
};

export default function ModalGrupo({
  aberto,
  codigo,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  /** `undefined` ou '0' = criar. Qualquer outro valor = editar. */
  codigo?: string | number;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const editando = Boolean(codigo && Number(codigo) > 0);

  const [form, setForm] = React.useState<Formulario>(VAZIO);
  const [original, setOriginal] = React.useState<any>({});
  const [salvando, setSalvando] = React.useState(false);
  const [carregando, setCarregando] = React.useState(false);

  const [vinculadas, setVinculadas] = React.useState<Apk[]>([]);
  // Texto com que o seletor de APK abriu; `null` = fechado.
  const [termoApk, setTermoApk] = React.useState<string | null>(null);

  // ---------------------------------------------------------------- carga
  React.useEffect(() => {
    if (!aberto) return;

    setForm(VAZIO);
    setOriginal({});
    setVinculadas([]);
    setTermoApk(null);

    if (!editando) return;

    setCarregando(true);
    axios
      .get<any>(`/entidades/id/${codigo}`)
      .then((r) => {
        const d = r.data || {};
        setOriginal(d);
        setForm({
          nome: d.nome ?? '',
          descricao: d.descricao ?? '',
          status: statusTexto(d.status),
          regional: d.regional ?? null,
          aplicativosBasicos: Boolean(d.aplicativosBasicos),
          // Nao vem do banco: o formulario antigo tambem nunca carregava este
          // (`setValue('aplicativosDadosMoveis', ...)` esta comentado la).
          aplicativosDadosMoveis: false,
        });
        setVinculadas(d.apks ?? []);
      })
      .catch((e: any) =>
        enqueueSnackbar(e?.response?.data?.message || 'Não foi possível carregar o grupo.', { variant: 'error' })
      )
      .finally(() => setCarregando(false));
  }, [aberto, codigo, editando, enqueueSnackbar]);

  /* SELETORES PAGINADOS, SO NA LUPA (21/10/2026). Antes o modal baixava TODAS
     as APKs ativas (rows=9999) e 200 regionais ao abrir, e de novo a cada pausa
     na busca de regional. Endpoints e filtros sao os mesmos:
       - APK: somente ATIVAS, igual ao antigo (`status=1`);
       - regional: `primeiro=true` tira a 37 ("todas") e `gerente` limita as que
         o usuario logado administra - os dois vem do formulario antigo. */
  const buscarApks = React.useMemo(
    () =>
      buscarPaginado(
        (termo, pagina, linhas) =>
          `/apks/paginado?page=${pagina}&rows=${linhas}&status=1&campo=${termo.includes('.') ? 'packageName' : 'nome'}` +
          `&valor=${encodeURIComponent(termo)}&direcao=asc&ordenar=nome`,
        (a) => ({ codigo: a.codigo, rotulo: a.nome || '—', secundario: a.packageName, dado: a })
      ),
    []
  );
  const buscarRegionais = React.useMemo(
    () =>
      buscarPaginado(
        (termo, pagina, linhas) =>
          `/regionais/paginado?page=${pagina}&rows=${linhas}&campo=nome&valor=${encodeURIComponent(termo)}` +
          `&direcao=asc&ordenar=nome&primeiro=true&gerente=${user?.codigo ?? -1}`,
        (r) => ({ codigo: r.codigo, rotulo: r.nome, dado: r })
      ),
    [user?.codigo]
  );

  // ------------------------------------------------------------------- QR
  const tokenPolitica = original?.politicas?.[0]?.tokenGoogle;

  const qrCode = React.useMemo(() => {
    if (!tokenPolitica) return '';
    try {
      const token = JSON.parse(tokenPolitica);

      // Chave PRESENTE quando marcado, REMOVIDA quando desmarcado - nao `false`.
      // E assim que o provisionador do Android trata estas duas.
      if (form.aplicativosDadosMoveis) token['android.app.extra.PROVISIONING_USE_MOBILE_DATA'] = true;
      else delete token['android.app.extra.PROVISIONING_USE_MOBILE_DATA'];

      if (form.aplicativosBasicos) token['android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED'] = true;
      else delete token['android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED'];

      return JSON.stringify(token);
    } catch {
      // Token invalido no banco: mostra o original em vez de quebrar a tela.
      return String(tokenPolitica);
    }
  }, [tokenPolitica, form.aplicativosBasicos, form.aplicativosDadosMoveis]);

  // ------------------------------------------------------------------ apks
  /* Confirmar no seletor devolve a lista INTEIRA marcada: as que ja estavam
     ficam como estavam (inclusive `novo`), as novas entram com `novo: true`. */
  function aoConfirmarApks(lista: OpcaoAuto[]) {
    setVinculadas(
      lista.map((o) => {
        const ja = vinculadas.find((v) => Number(v.codigo) === Number(o.codigo));
        if (ja) return ja;
        const a = o.dado || {};
        return { codigo: a.codigo, nome: a.nome, packageName: a.packageName, url: a.url, novo: true };
      })
    );
  }

  function desvincular(apk: Apk) {
    setVinculadas((v) => v.filter((x) => Number(x.codigo) !== Number(apk.codigo)));
  }

  // --------------------------------------------------------------- validacao
  const nomeOk = form.nome.trim().length > 0 && form.nome.trim().length <= 150;
  const descricaoOk = form.descricao.trim().length > 0 && form.descricao.trim().length <= 150;

  // --------------------------------------------------------------- salvar
  async function salvar() {
    if (salvando) return;
    // Edicao sem o grupo carregado (falha de rede, 403 de outra regional): salvar
    // gravaria o formulario vazio/digitado por cima do grupo real.
    if (editando && !original?.codigo) {
      enqueueSnackbar('O grupo não foi carregado. Feche e abra de novo antes de salvar.', { variant: 'error' });
      return;
    }
    setSalvando(true);
    try {
      const diff: Array<{ campo: string; antigo: any; novo: any }> = [];
      if (editando) {
        const cmp = (campo: string, antigo: any, novo: any) => {
          if (String(antigo ?? '') !== String(novo ?? '')) diff.push({ campo, antigo, novo });
        };
        cmp('Nome', original.nome, form.nome);
        cmp('Descricao', original.descricao, form.descricao);
        cmp('Status', original.status, form.status);
        cmp('Regional', original.regional?.nome || ' - ', form.regional?.nome || ' - ');
        cmp('aplicativosBasicos', String(original.aplicativosBasicos), String(form.aplicativosBasicos));
      }

      const corpo: any = {
        codigo: editando ? Number(codigo) : 0,
        nome: form.nome.trim(),
        descricao: form.descricao.trim(),
        status: form.status,
        aplicativosBasicos: form.aplicativosBasicos,
        regional: form.regional,
        // Somente quando ha regional escolhida - mesma condicao do antigo.
        codRegional: form.regional?.nome ? form.regional.codigo : null,
        // `null` na criacao, igual ao antigo: sem registro anterior nao existe diff.
        dataCampo: editando ? JSON.stringify(diff) : null,
        apks: vinculadas,
      };
      // `aplicativosDadosMoveis` fica FORA de proposito - ver o cabecalho.

      await axios[editando ? 'put' : 'post']('/entidades', corpo);

      enqueueSnackbar('Salvo com sucesso', { variant: 'success' });
      onSalvo?.();
      onFechar();
    } catch (err: any) {
      const e = err?.response?.data;
      const msg = e?.errors?.length > 0 ? e.errors[0].message : e?.message;
      enqueueSnackbar(msg || 'Ops ocorreu um erro no sistema.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setSalvando(false);
    }
  }

  // -------------------------------------------------------------- cartoes
  const passos: PassoModal[] = [
    {
      id: 'dados',
      titulo: 'Dados',
      grupo: 'Cadastro',
      resumo: form.nome.trim() || 'sem nome',
      valido: nomeOk && descricaoOk,
      pendencia: !nomeOk ? 'nome' : 'descrição',
      conteudo: (
        <>
          <Campo rotulo="Nome" obrigatorio>
            <input
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              maxLength={150}
              className={classesEntrada}
            />
          </Campo>

          <Campo rotulo="Descrição" obrigatorio dica="Aparece na listagem e ajuda a distinguir grupos parecidos.">
            <input
              value={form.descricao}
              onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
              maxLength={150}
              className={classesEntrada}
            />
          </Campo>

          <Campo bloco rotulo="Status">
            <SeletorStatus valor={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} />
          </Campo>

          {editando && (
            <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
              Grupo <Num className="text-ink">{original.codigo}</Num> ·{' '}
              <Num className="text-ink">{original.apks?.length ?? 0}</Num> APK(s) vinculada(s)
            </div>
          )}

          {/* ATALHO PARA A POLITICA (29/09/2026, a pedido).

              O link existia, mas dentro do passo "Regional" - visivel so para
              quem clicasse ali. O modal abre em "Dados", e e daqui que se quer
              pular para a regra que o grupo aplica.

              Grupo e politica sao um par: quem abre um quase sempre precisa
              conferir o outro, e o caminho era voltar a lista, trocar de tela e
              procurar pelo nome. O par de ida e volta esta fechado - a tela de
              politica tem o link de volta para o grupo.

              Rota `politicas-novo/new/<name>`: a antiga (`politicas/new/...`)
              foi removida em 19/08 e dava 404. */}
          {editando && original?.politicas?.[0] && (
            <NextLink
              href={`/dashboard/politicas-novo/new/${encodeURIComponent(original.politicas[0].name)}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface-2 p-3 hover:border-primary"
            >
              <span className="min-w-0">
                <span className="block text-[12px] font-bold">Política do grupo</span>
                <span className="block break-all text-[11px] text-muted-foreground">
                  {original.politicas[0].url || original.politicas[0].name}
                </span>
              </span>
              <span aria-hidden className="flex-none text-[13px] text-primary">→</span>
            </NextLink>
          )}

          {/* So depois de carregar: com falha, `original` fica {} e isto afirmava um fato falso. */}
          {editando && !!original?.codigo && !original?.politicas?.[0] && (
            <div className="rounded-lg border border-line bg-warn-soft p-3 text-[12px] text-warn">
              Este grupo não tem política vinculada — nenhuma regra chega aos aparelhos dele.
            </div>
          )}
        </>
      ),
    },

    {
      id: 'regional',
      /* Rotulo "Regional", e nao "Regional e política": a politica aqui e um
         LINK de leitura, nao campo editavel - quem edita politica faz isso na tela
         de politicas. O nome antigo prometia duas coisas e entregava uma, e ainda
         repetia a palavra logo abaixo, no titulo da secao. */
      titulo: 'Regional',
      grupo: 'Cadastro',
      /* REGIONAL PASSOU A SER OBRIGATORIA (28/09/2026, a pedido).

         Antes era opcional "porque o formulario antigo nao exigia e o payload
         aceita null". O resultado em producao: grupo salvo sem regional some
         para todo mundo que nao tem acesso a todas - inclusive para quem acabou
         de cria-lo. O aviso amarelo explicava isso e nao impedia nada, e um
         aviso que so explica o prejuizo depois nao serve de muito.

         O BACKEND CONTINUA ACEITANDO `codRegional: null`, de proposito: os
         grupos ja gravados sem regional continuam abrindo e salvando por outras
         vias. A exigencia e da TELA DE CADASTRO, que e onde o erro nascia. */
      valido: Boolean(form.regional),
      pendencia: 'Regional',
      conteudo: (
        <>
          <div className="mb-1 flex items-center justify-between">
            <span className="flex items-center text-[12px] font-semibold">
              Regional
              <span className="ml-1 text-danger">*</span>
            </span>
            <span className="text-[11px] text-muted-foreground">
              {form.regional ? form.regional.nome : 'nenhuma'}
            </span>
          </div>

          <AutoCompleteUnico
            selecionado={form.regional ? { codigo: form.regional.codigo, rotulo: form.regional.nome } : null}
            // O registro do servidor vai inteiro em `regional` no corpo, como antes.
            aoEscolher={(o) => setForm((f) => ({ ...f, regional: o ? o.dado : null }))}
            buscar={buscarRegionais}
            titulo="Selecionar regional"
            placeholder="Nome da regional (Enter ou lupa)"
            vazio="Nenhuma regional encontrada."
          />

          {!form.regional && (
            <div className="rounded-lg border border-line bg-warn-soft p-3 text-[12px] text-warn">
              Escolha a regional para continuar. Sem ela, o grupo só apareceria para
              quem tem acesso a todas — nem você mesmo o encontraria depois.
            </div>
          )}

          {/* O link da politica SAIU daqui e foi para o passo "Dados"
              (29/09/2026, relatado): e o passo em que o modal abre, e era la que
              ele estava fazendo falta. Aqui ficava visivel so para quem clicasse
              em "Regional" - ou seja, quase nunca. */}
        </>
      ),
    },

    {
      id: 'apks',
      titulo: 'APKs do grupo',
      grupo: 'Cadastro',
      opcional: true,
      conteudo: (
        <>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[12px] font-semibold">Vinculadas</span>
            <Num className="text-[11px] text-muted-foreground">{vinculadas.length}</Num>
          </div>

          <div className="mb-3 max-h-[150px] overflow-y-auto rounded-lg border border-line">
            {vinculadas.map((a) => (
              <div
                key={a.codigo}
                className="flex items-center gap-2 border-b border-line px-2.5 py-1.5 last:border-b-0"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] font-semibold">{a.nome || '—'}</span>
                  <Num className="block truncate text-[11px] text-faint">{a.packageName || '—'}</Num>
                </span>
                {a.url && (
                  <a
                    download
                    href={`${process.env.HOST_API_APK}/${a.url}`}
                    className="flex-none text-[11px] font-semibold text-primary hover:underline"
                  >
                    baixar
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => desvincular(a)}
                  className="flex-none text-[11px] font-semibold text-danger"
                >
                  remover
                </button>
              </div>
            ))}
            {!vinculadas.length && (
              <div className="px-3 py-4 text-center text-[12px] text-faint">Nenhuma APK vinculada.</div>
            )}
          </div>

          <Campo bloco rotulo="Adicionar APK" dica="Somente APKs com status ativo aparecem aqui.">
            <CampoLupa placeholder="Nome ou pacote (Enter ou lupa)" aoAbrir={setTermoApk} />
          </Campo>

          {termoApk !== null && (
            <ModalSelecionar
              multiplo
              titulo="Selecionar APKs"
              buscar={buscarApks}
              inicial={vinculadas.map((a) => ({ codigo: a.codigo, rotulo: a.nome || '—', secundario: a.packageName }))}
              termoInicial={termoApk}
              placeholder="Nome ou pacote"
              vazio="Nenhuma APK ativa encontrada."
              aoConfirmar={aoConfirmarApks}
              onFechar={() => setTermoApk(null)}
            />
          )}
        </>
      ),
    },
  ];

  // O bloco de provisionamento SO existe quando ha token de politica - sem ele
  // nao ha QR Code para gerar, e os dois interruptores nao teriam efeito nenhum.
  if (editando && tokenPolitica) {
    passos.push({
      id: 'provisionamento',
      titulo: 'Provisionamento (QR)',
      grupo: 'Ferramentas',
      resumo: 'QR e link',
      opcional: true,
      conteudo: (
        <>
          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line p-2.5">
            <input
              type="checkbox"
              checked={form.aplicativosBasicos}
              onChange={(e) => setForm((f) => ({ ...f, aplicativosBasicos: e.target.checked }))}
              className="mt-0.5 h-3.5 w-3.5 accent-[hsl(var(--primary))]"
            />
            <span>
              {/* LEGENDA CORRIGIDA (28/09/2026, relatado por quem usa).

                  Dizia "Inativa a opção de restauração de fábrica no aparelho",
                  que e outra coisa inteiramente. Este campo escreve
                  PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED no token do QR Code
                  (ver a montagem do token acima), e o que essa chave faz e
                  MANTER os aplicativos de fabrica depois do enrolamento.

                  Sem ela o provisionamento do Android desativa os apps que vem
                  no aparelho - telefone, camera, galeria, calculadora - e o
                  usuario recebe um celular que so tem o que a politica instalou.
                  Nada disso tem a ver com reset de fabrica.

                  Legenda errada em campo que altera o que chega ao aparelho e
                  pior que legenda nenhuma: quem marcava esperando travar o reset
                  ficava sem a protecao que achava ter, e ainda mudava o
                  provisionamento sem querer. */}
              <span className="block text-[12px] font-semibold">Manter apps de fábrica do aparelho</span>
              <span className="block text-[11px] text-muted-foreground">
                Telefone, câmera, galeria e demais apps que já vêm no celular continuam
                disponíveis após o enrolamento. Desmarcado, o Android os desativa.
              </span>
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line p-2.5">
            <input
              type="checkbox"
              checked={form.aplicativosDadosMoveis}
              onChange={(e) => setForm((f) => ({ ...f, aplicativosDadosMoveis: e.target.checked }))}
              className="mt-0.5 h-3.5 w-3.5 accent-[hsl(var(--primary))]"
            />
            <span>
              <span className="block text-[12px] font-semibold">Forçar dados móveis</span>
              <span className="block text-[11px] text-muted-foreground">
                Para quando o aparelho não está lendo o QR pela rede Wi-Fi.
              </span>
            </span>
          </label>

          <div className="grid place-items-center rounded-lg border border-line bg-white p-4">
            {qrCode ? (
              // `size` e a prop do react-qr-code. A tela antiga usava
              // `style={{width:256}}`, que brigava com o height padrao (256) e
              // deformava o codigo em telas estreitas.
              <QRCode level="Q" size={232} value={qrCode} />
            ) : (
              <span className="text-[12px] text-faint">Sem token de política.</span>
            )}
          </div>

          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
            Os dois interruptores mudam <strong>somente este QR Code</strong> — o segundo não é gravado no
            cadastro. Reflita a mudança lendo o código novo no aparelho.
          </div>
        </>
      ),
    });
  }

  passos.push({
    id: 'historico',
    titulo: 'Histórico',
    grupo: 'Ferramentas',
    informativo: true,
    conteudo: original.codigo ? (
      <HistoricoGPS tipo={'ENTIDADE'} chave={original.codigo} />
    ) : (
      <div className="rounded-lg border border-line bg-surface-2 p-4 text-[12px] text-muted-foreground">
          O histórico começa a ser gravado quando este cadastro for salvo. Cada alteração
          posterior fica registrada aqui com quem fez, quando e o que mudou.
        </div>
    ),
  });

  return (
    <ModalPassos
      aberto={aberto}
      titulo={editando ? 'Editar grupo' : 'Novo grupo'}
      subtitulo={carregando ? 'Carregando...' : editando ? original.nome || `Grupo ${codigo}` : 'Novo grupo'}
      passos={passos}
      rotuloFinalizar={editando ? 'Salvar' : 'Criar grupo'}
      onFinalizar={salvar}
      onFechar={onFechar}
      salvando={salvando}
    />
  );
}
